import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, Send, Square, Volume2, Repeat2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEMO_AGENT_CACHE_KEY = 'landing.demo.agentId.v1';

function loadCachedAgentId() {
  try {
    return localStorage.getItem(DEMO_AGENT_CACHE_KEY) || null;
  } catch {
    return null;
  }
}

export default function LandingDemo() {
  const [agentId, setAgentId] = useState(loadCachedAgentId);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('');
  const [connected, setConnected] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [convoMode, setConvoMode] = useState(false);
  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const msgsContainerRef = useRef(null);
  const convoModeRef = useRef(false);

  const scrollToBottom = () => {
    if (msgsContainerRef.current) msgsContainerRef.current.scrollTop = msgsContainerRef.current.scrollHeight;
  };
  useEffect(scrollToBottom, [messages]);
  useEffect(() => { convoModeRef.current = convoMode; }, [convoMode]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/demo/agent`);
        const data = await res.json();
        if (data.agent_id) {
          setAgentId(data.agent_id);
          try { localStorage.setItem(DEMO_AGENT_CACHE_KEY, data.agent_id); } catch {}
        }
      } catch {}
    })();
  }, []);

  const startRecording = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setStatus('Speech recognition not supported. Use Chrome or Edge.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'text', text }));
        setStatus('Processing...');
      }
    };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  const connect = useCallback(() => {
    if (!agentId) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/conversation/${agentId}`);
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'welcome') {
        setMessages([{ role: 'assistant', content: data.message }]);
        setStatus('');
      } else if (data.type === 'transcription') {
        setMessages(prev => [...prev, { role: 'user', content: data.text }]);
        setStatus('');
      } else if (data.type === 'status') {
        setStatus(data.message);
      } else if (data.type === 'ai_response') {
        setMessages(prev => [...prev, { role: 'assistant', content: data.text }]);
        setStatus('');
      } else if (data.type === 'audio') {
        const audio = new Audio(`data:audio/mp3;base64,${data.data}`);
        audioRef.current = audio;
        setIsPlaying(true);
        audio.onended = () => setIsPlaying(false);
        audio.play().catch(() => setIsPlaying(false));
      } else if (data.type === 'audio_end') {
        setIsPlaying(false);
        if (convoModeRef.current) setTimeout(() => startRecording(), 300);
      }
    };
    wsRef.current = ws;
  }, [agentId, startRecording]);

  useEffect(() => {
    connect();
    return () => wsRef.current?.close();
  }, [connect]);

  const toggleMic = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const sendText = () => {
    if (!inputText.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'text', text: inputText.trim() }));
    setInputText('');
    setStatus('Processing...');
  };

  if (!agentId) {
    return (
      <div className="rounded-lg bg-elevated p-8 min-h-[360px] flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5" />
        <div className="relative z-10 text-center">
          <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Mic className="text-accent" size={28} />
          </div>
          <p className="text-zinc-500 text-sm">Loading demo...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="landing-demo-widget" className="rounded-lg bg-elevated relative overflow-hidden flex flex-col" style={{ height: '420px' }}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/3 pointer-events-none" />

      {/* Demo Header */}
      <div className="relative z-10 flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              <Volume2 size={14} className="text-accent" />
            </div>
            {isPlaying && (
              <div className="absolute -inset-1 rounded-full border border-accent/40 animate-ping" />
            )}
          </div>
          <div>
            <span className="text-sm font-heading font-medium text-white">Front Demo</span>
            <span className="block text-[10px] text-zinc-500">
              {connected ? 'Live' : 'Connecting...'}
              {connected && <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent ml-1.5 align-middle" />}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConvoMode(m => !m)}
            title={convoMode ? 'Conversation mode on — click to disable' : 'Enable conversation mode'}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full transition-all"
            style={{
              background: convoMode ? 'rgba(0,214,126,0.15)' : 'rgba(255,255,255,0.05)',
              border: convoMode ? '1px solid rgba(0,214,126,0.35)' : '1px solid rgba(255,255,255,0.1)',
              color: convoMode ? '#00D67E' : '#666',
            }}
          >
            <Repeat2 size={12} />
            Convo
          </button>
          <span className="text-[10px] font-mono text-zinc-600 border border-white/10 rounded px-2 py-0.5">DEMO</span>
        </div>
      </div>

      {/* Messages */}
      <div ref={msgsContainerRef} className="relative z-10 flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-up`}>
            <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-accent/15 text-white border border-accent/20'
                : 'bg-white/[0.04] text-zinc-300 border border-white/5'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {status && (
          <div className="flex justify-start">
            <div className="bg-white/[0.04] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-zinc-500">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-accent/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-accent/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-accent/50 animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Recording indicator */}
      {isRecording && (
        <div className="relative z-10 px-5 py-2 flex items-center justify-center gap-1.5 bg-red-500/5 border-t border-red-500/10">
          {[...Array(16)].map((_, i) => (
            <div key={i} className="w-0.5 bg-red-400/70 rounded-full animate-pulse" style={{
              height: `${Math.random() * 18 + 4}px`,
              animationDelay: `${i * 0.06}s`
            }} />
          ))}
          <span className="text-red-400 text-[11px] ml-2 font-mono">REC</span>
        </div>
      )}

      {/* Input Area */}
      <div className="relative z-10 px-4 py-3 border-t border-white/10 bg-base/50">
        <div className="flex items-center gap-2">
          <button
            data-testid="demo-mic-btn"
            onClick={toggleMic}
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
              isRecording
                ? 'bg-red-500 shadow-lg shadow-red-500/30'
                : 'bg-accent/10 border border-accent/30 hover:bg-accent/20'
            }`}
          >
            {isRecording ? <Square size={16} className="text-white" /> : <Mic size={16} className="text-accent" />}
          </button>
          <input
            data-testid="demo-text-input"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendText()}
            className="flex-1 bg-surface border border-white/10 text-white rounded-lg px-3.5 py-2.5 text-sm focus:border-accent/50 outline-none transition-colors placeholder-zinc-600"
            placeholder="Type a message or click mic..."
          />
          <button
            data-testid="demo-send-btn"
            onClick={sendText}
            className="w-10 h-10 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0 hover:bg-accent/20 transition-colors"
          >
            <Send size={16} className="text-accent" />
          </button>
        </div>
      </div>
    </div>
  );
}
